import React, { useState, useEffect } from 'react';
const useId = () => React.useMemo(() => "id_" + Math.random().toString(36).substr(2, 9), []);
import styles from './ForensicGauge.module.css';

const ForensicGauge = ({ value = 0, forcedValue = null, premiumValue = null, isMember = false }) => {
    const [animatedRotation, setAnimatedRotation] = useState(-90);
    const [isAnimating, setIsAnimating] = useState(false);
    const numericValue = parseFloat(value) || 0;
    const uniqueId = useId();
    const idSafe = uniqueId.replace(/:/g, ''); // Remove colons for SVG ID safety

    // Automated Calculations based on attr_base_value
    const fVal = (forcedValue && parseFloat(forcedValue) > 0) ? parseFloat(forcedValue) : (numericValue * 0.8);
    const pVal = (premiumValue && parseFloat(premiumValue) > 0) ? parseFloat(premiumValue) : (numericValue * 1.25);

    const formatValue = (val) => {
        const num = parseFloat(val) || 0;
        const rawValue = num > 100000 ? num : num * 1000000;

        if (rawValue >= 1000000000) {
            const billions = rawValue / 1000000000;
            return `${billions % 1 === 0 ? billions.toFixed(0) : billions.toFixed(1)}B`;
        } else {
            const millions = rawValue / 1000000;
            // Show 1 decimal if it's not a whole number (e.g. 0.8M), otherwise show 0 (e.g. 1M)
            return `${millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)}M`;
        }
    };

    const forcedSale = formatValue(fVal);
    const premium = formatValue(pVal);
    const mainDisplay = formatValue(numericValue);

    // Needle Logic: Calculate position within the spread
    const spread = pVal - fVal;
    const position = (spread > 0 && numericValue > 0) ? (numericValue - fVal) / spread : 0;
    const percentage = (numericValue > 0) ? Math.max(0, Math.min(1, position)) : 0;

    // Only sweep to the target if the user is a member and percentage > 0, otherwise stay at 0 (-90 degrees)
    const targetRotation = (isMember && percentage > 0) ? (-90 + (percentage * 180)) : -90;

    useEffect(() => {
        if (isMember && percentage > 0) {
            // Reset to start for a fresh sweep when member status is unlocked
            setIsAnimating(false);
            setAnimatedRotation(-90);

            const timer = setTimeout(() => {
                setIsAnimating(true);
                setAnimatedRotation(targetRotation);
            }, 100);
            return () => clearTimeout(timer);
        } else {
            // Stay at start position
            setAnimatedRotation(-90);
            setIsAnimating(false);
        }
    }, [targetRotation, isMember, percentage]);

    const renderDefs = (suffix) => (
        <defs>
            <linearGradient id={`goldGradient-${suffix}-${idSafe}`} x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#A76700" />
                <stop offset="100%" stopColor="#FFC971" />
            </linearGradient>

            <filter id={`goldGlow-${suffix}-${idSafe}`} x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feFlood floodColor="var(--primary-color)" floodOpacity="0.4" result="color" />
                <feComposite in="color" in2="blur" operator="in" result="glow" />
                <feMerge>
                    <feMergeNode in="glow" />
                    <feMergeNode in="SourceGraphic" />
                </feMerge>
            </filter>
        </defs>
    );

    return (
        <>
            <div className={`${styles['gauge-svg-wrapper']} ${styles['gauge-desktop']}`}>
                <svg viewBox="-55 0 310 110" style={{ maxWidth: '650px', width: '100%', height: 'auto', display: 'block' }}>
                    {renderDefs('desktop')}

                    {[...Array(40)].map((_, i) => {
                        const angle = -90 + (i * 180) / 39;
                        const isActive = isMember && (i / 40) <= percentage;
                        const opacity = isActive ? 1 : 0.4;

                        return (
                            <rect
                                key={i}
                                x="97.5"
                                y="5"
                                width="5"
                                height="20"
                                rx="2"
                                fill={isActive ? `url(#goldGradient-desktop-${idSafe})` : "#333"}
                                filter={isActive ? `url(#goldGlow-desktop-${idSafe})` : "none"}
                                style={{
                                    transform: `rotate(${angle}deg)`,
                                    transformOrigin: '100px 100px',
                                    opacity: opacity,
                                    transition: `all 0.5s ease ${i * 0.02}s`
                                }}
                            />
                        );
                    })}

                    {/* Needle moved to end to stay on top of everything */}
                    <g
                        transform={`rotate(${animatedRotation}, 100, 100)`}
                        style={{
                            transition: isAnimating ? 'transform 5s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'
                        }}
                    >
                        <path
                            d="M 98 100 L 100 10 L 102 100 Z"
                            fill={isMember ? "#ffffff" : "#333"}
                            style={{ transition: 'fill 0.8s ease' }}
                        />
                        <circle cx="100" cy="100" r="4" fill={isMember ? "var(--gold)" : "#222"} />
                    </g>

                    <g transform="translate(100, 95)">
                        <text
                            fill={isMember ? "var(--gold)" : "#444"}
                            fontSize="26"
                            fontWeight="900"
                            textAnchor="middle"
                            style={{ transition: 'fill 0.8s ease' }}
                        >
                            £{mainDisplay}
                        </text>
                        <text
                            y="12"
                            fill="#888"
                            fontSize="6"
                            fontWeight="bold"
                            textAnchor="middle"
                            style={{ textTransform: 'uppercase', letterSpacing: '1px' }}
                        >
                            Current Forensic Value
                        </text>
                    </g>



                    <g style={{ opacity: isMember ? 1 : 0.4, transition: 'opacity 0.8s ease' }}>
                        {/* Left Side: Forced Sale */}
                        <text x="-45" y="85" fill="#888" fontSize="5.5" fontWeight="bold">FORCED SALE</text>
                        <text x="-45" y="100" fill="#fff" fontSize="12" fontWeight="900">£{forcedSale}</text>

                        {/* Right Side: Premium */}
                        <text x="245" y="85" fill="#888" fontSize="5.5" fontWeight="bold" textAnchor="end">PREMIUM</text>
                        <text x="245" y="100" fill="#fff" fontSize="12" fontWeight="900" textAnchor="end">£{premium}</text>
                    </g>
                </svg>
            </div>

            <div className={`${styles['gauge-svg-wrapper']} ${styles['gauge-mobile']}`}>
                <svg viewBox="8 0 330 215" className={styles['forensic-gauge-svg']}>
                    {renderDefs('mobile')}

                    {[...Array(40)].map((_, i) => {
                        const angle = -90 + (i * 180) / 39;
                        const isActive = isMember && (i / 39) <= percentage;
                        const opacity = isActive ? 1 : 0.32;

                        return (
                            <rect
                                key={i}
                                x="169"
                                y="5"
                                width="8"
                                height="28"
                                rx="2"
                                fill={isActive ? `url(#goldGradient-mobile-${idSafe})` : "#2a2a2a"}
                                filter={isActive ? `url(#goldGlow-mobile-${idSafe})` : "none"}
                                style={{
                                    transform: `rotate(${angle}deg)`,
                                    transformOrigin: '173px 150px',
                                    opacity: opacity,
                                    transition: `fill 0.5s ease ${i * 0.02}s, opacity 0.5s ease ${i * 0.02}s`
                                }}
                            />
                        );
                    })}

                    <g transform="translate(173, 115)">
                        <text
                            fill={isMember ? "var(--gold)" : "#444"}
                            fontSize="36"
                            fontWeight="700"
                            textAnchor="middle"
                            style={{ transition: 'fill 0.8s ease' }}
                        >
                            £{mainDisplay}
                        </text>
                        <text
                            y="22"
                            fill="#888"
                            fontSize="14"
                            fontWeight="400"
                            textAnchor="middle"
                            style={{ textTransform: 'uppercase', letterSpacing: '1px' }}
                        >
                            Current Forensic Value
                        </text>
                    </g>

                    <g style={{ opacity: isMember ? 1 : 0.4, transition: 'opacity 0.8s ease' }}>
                        {/* Left Side */}
                        <text x="42" y="184" fill="#fff" fontSize="20" fontWeight="700" textAnchor="middle">£{forcedSale}</text>
                        <text x="42" y="200" fill="#888" fontSize="11" fontWeight="bold" textAnchor="middle">FORCED SALE</text>

                        {/* Right Side */}
                        <text x="304" y="184" fill="#fff" fontSize="20" fontWeight="700" textAnchor="middle">£{premium}</text>
                        <text x="304" y="200" fill="#888" fontSize="11" fontWeight="bold" textAnchor="middle">PREMIUM</text>
                    </g>

                    {/* Needle moved to end to stay on top of everything */}
                    <g
                        transform={`rotate(${animatedRotation}, 173, 150)`}
                        style={{
                            transition: isAnimating ? 'transform 5s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'
                        }}
                    >
                        <path
                            d="M 171.5 150 L 173 64 L 174.5 150 Z"
                            fill={isMember ? "#ffffff" : "#333"}
                            style={{ transition: 'fill 0.8s ease' }}
                        />
                        <circle cx="173" cy="150" r="5" fill={isMember ? "var(--gold)" : "#222"} />
                    </g>
                </svg>
            </div>
        </>
    );
};

export default ForensicGauge;
